import { milestoneBadgeStatusMap } from '@/constants/milestones';
import { getAfterCompletionRedirectPath } from '@/helpers/getAfterCompletionRedirectPath';
import { resolveMilestonePackageRoute } from '@/helpers/milestonePackageRoute';
import usePermissions from '@/hooks/usePermissions';
import { useGetPerformProgressQuery } from '@/services/organizations';
import { Badge, Box, Button, Card, useBreakpoint } from '@palmetto/palmetto-components';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  AccountMilestone,
  AccountRequirementDocument,
  AccountRequirementStatus,
  MilestonePackageRoute,
  MilestoneRequirement,
  MilestoneType,
  Packages,
  UserPermissions,
} from 'types';

import { useGetAccountQuery } from '../../services/accounts';

export const PostActivationMilestonePackageContainer = () => {
  const { id } = useParams<{ id: any }>();
  const { data: account } = useGetAccountQuery(id);
  const navigate = useNavigate();
  const { isPhone } = useBreakpoint();
  const { data: canPerformProgressBasedOnOrgStatus } = useGetPerformProgressQuery(id);
  const userPermissions = usePermissions();

  const postActivationPackageRequirement = useMemo(() => {
    if (!account) {
      return;
    }

    const postActivationMilestone = account.milestones?.find(
      (milestone: AccountMilestone) => milestone.type === MilestoneType.postActivation,
    );

    const postActivationPackageRequirement: AccountRequirementDocument = postActivationMilestone?.requirements.find(
      (requirement: AccountRequirementDocument) => requirement.type === MilestoneRequirement.postActivationPackage,
    );
    return postActivationPackageRequirement;
  }, [account]);

  const isLightReachQCTech = userPermissions.includes(UserPermissions.lightReachQCTech);
  const postActivationPackageStatus = postActivationPackageRequirement
    ? postActivationPackageRequirement.status
    : AccountRequirementStatus.pending;
  const isPostActivationPackageCreated = postActivationPackageStatus !== AccountRequirementStatus.pending;

  const handleAfterCompletionRedirect = () => {
    const routePath = getAfterCompletionRedirectPath(
      id,
      MilestonePackageRoute.postActivationPackage,
      isLightReachQCTech,
    );
    navigate(routePath);
  };

  const handleCreateClick = () => {
    const route = resolveMilestonePackageRoute({
      accountId: account?.id,
      programType: account?.programType,
      packageType: Packages.postActivationPackage,
    });
    navigate(route.path, route.state ? { state: route.state } : undefined);
  };

  // if all the documents exist in the completed group then the post activation package is complete

  return (
    <Card margin="0 0 lg 0">
      <Box childGap="lg" padding="lg" alignItems="center" direction="row" justifyContent="space-between">
        <Box as="h3" fontWeight="medium" fontSize="md">
          Post Activation Package
        </Box>
        {!isPostActivationPackageCreated && (
          <Button
            as="button"
            disabled={!canPerformProgressBasedOnOrgStatus}
            navigate={handleCreateClick}
            size="sm"
            variant="primary"
            tone="neutral"
            iconPrefix="add"
          >
            {!isPhone && 'Post Activation Package'}
          </Button>
        )}
      </Box>
      <Box
        borderWidth="xs 0 0 0"
        borderColor="separator"
        background="primary"
        padding={{ base: 'md 0', tablet: '0' }}
        direction="row"
        width="100"
        alignItems="center"
      >
        {isPostActivationPackageCreated ? (
          <>
            <Box width={{ base: '100', tablet: '60' }} direction={{ base: 'column', tablet: 'row' }}>
              <Box
                alignItems="flex-start"
                width={{ base: '100', tablet: '30' }}
                padding={{ base: 'md lg', tablet: 'md md md lg' }}
              >
                <Badge
                  message={milestoneBadgeStatusMap[postActivationPackageStatus].label}
                  variant={milestoneBadgeStatusMap[postActivationPackageStatus].variant}
                />
              </Box>
              <Box
                width={{ base: '100', tablet: '70' }}
                display="block"
                childGap="xs"
                padding={{ base: '0 lg', tablet: 'md md md lg' }}
              >
                <Box fontWeight="medium">Post Activation Package</Box>
              </Box>
            </Box>
            <Box
              width="40"
              padding={{ base: '0 lg', tablet: 'md lg' }}
              direction={{ base: 'column', tablet: 'row' }}
              childGap="xs"
              justifyContent="flex-end"
              alignItems="flex-end"
            >
              {postActivationPackageStatus !== AccountRequirementStatus.completed && (
                <Button
                  size="sm"
                  variant="secondary"
                  tone="neutral"
                  onClick={() => navigate(`post-activation-package/edit`)}
                >
                  Edit
                </Button>
              )}
              {postActivationPackageStatus === AccountRequirementStatus.completed && (
                <Button size="sm" variant="secondary" tone="neutral" onClick={handleAfterCompletionRedirect}>
                  View
                </Button>
              )}
            </Box>
          </>
        ) : null}
      </Box>
    </Card>
  );
};
